import { ReactNode, useRef } from 'react'
import { useIntl } from 'react-intl'
import { Button } from 'primereact/button'
import { ProgressSpinner } from 'primereact/progressspinner'
import { Sidebar } from 'primereact/sidebar'
import {
  HospitalizationBreakdownRowDTO,
  HospitalizationDailyPointDTO,
  HospitalizationFilterDTO,
  HospitalizationSectorOccupancyDTO,
} from '../../../dto/hospitalizationSummary/HospitalizationSummaryDTO'
import { getHospitalizationSummary } from '../../../services/HospitalizationSummaryController'
import useSectionData from '../../UrgencySummary/useSectionData'
import { filtersKey } from '../hospitalizationFilterModel'
import { dayTitleOf } from '../hospitalizationEvolutionModel'
import { formatCount, formatPercent } from '../hospitalizationSummaryFormat'
import HospitalizationBars, { HospitalizationBarRow } from './HospitalizationBars'

interface HospitalizationDaySidebarProps {
  /** El día que se mira (el punto del gráfico); null con el panel cerrado. */
  day: HospitalizationDailyPointDTO | null
  /** Los filtros aplicados de la pantalla: el panel pone las fechas del día. */
  filters: HospitalizationFilterDTO
  /** «Ver solo este día»: aplica la fecha como período de la pantalla. */
  onViewDay: (date: string) => void
  onHide: () => void
}

interface KpiProps {
  label: string
  value: ReactNode
}

function Kpi({ label, value }: KpiProps) {
  return (
    <div className="nv-hosp-detail__kpi">
      <dt className="nv-hosp-detail__kpi-label">{label}</dt>
      <dd className="nv-hosp-detail__kpi-value">{value}</dd>
    </div>
  )
}

interface SectionProps {
  id: string
  title: string
  children: ReactNode
}

function Section({ id, title, children }: SectionProps) {
  return (
    <section className="nv-hosp-detail__section" aria-labelledby={id}>
      <h3 className="nv-section-title" id={id}>{title}</h3>
      {children}
    </section>
  )
}

/** Detalle de un día del gráfico de evolución, en un panel lateral de Novarium (420 px, a todo el ancho en pantallas angostas). */
export default function HospitalizationDaySidebar({ day, filters, onViewDay, onHide }: HospitalizationDaySidebarProps) {
  const intl = useIntl()
  // Mientras el panel se cierra se sigue mostrando lo último (sin parpadeo de «Cargando»)
  const lastShown = useRef<{ day: HospitalizationDailyPointDTO; filters: HospitalizationFilterDTO } | null>(null)
  if (day) lastShown.current = { day, filters }
  const shown = day ? { day, filters } : lastShown.current

  const dayFilters: HospitalizationFilterDTO | null = shown ? { ...shown.filters, from: shown.day.date, to: shown.day.date } : null
  const summary = useSectionData(
    dayFilters === null ? null : `${filtersKey(dayFilters)}|day`,
    (signal) => {
      if (!dayFilters) throw new Error('Panel sin día')
      return getHospitalizationSummary(dayFilters, signal)
    },
  )

  if (!shown) return null
  const title = dayTitleOf(intl, shown.day.date)
  const point = shown.day
  const data = summary.data

  /** Un reparto con barras «X de N»: sin las categorías en cero y sin la sección si no queda ninguna. */
  const breakdown = (id: string, titleId: string, rows: HospitalizationBreakdownRowDTO[], total: number): ReactNode => {
    const shownRows: HospitalizationBarRow[] = rows
      .filter((row) => row.quantity > 0)
      .map((row) => ({
        key: String(row.id ?? row.name),
        label: row.name,
        fraction: total > 0 ? row.quantity / total : 0,
        valueText: intl.formatMessage({ id: 'HospitalizationSummaryOf' }, {
          count: formatCount(intl, row.quantity), total: formatCount(intl, total),
        }),
      }))
    if (shownRows.length === 0) return null
    return (
      <Section id={id} title={intl.formatMessage({ id: titleId })}>
        <HospitalizationBars rows={shownRows} ariaLabelledBy={id} />
      </Section>
    )
  }

  const sectorRows = (sectors: HospitalizationSectorOccupancyDTO[]): HospitalizationBarRow[] =>
    sectors
      .filter((sector) => sector.bedDays > 0)
      .map((sector) => ({
        key: String(sector.sectorId),
        label: sector.nursery
          ? intl.formatMessage({ id: 'HospitalizationSummaryNurseryName' }, { name: sector.sectorName })
          : sector.sectorName,
        fraction: (sector.occupancyPct ?? 0) / 100,
        valueText: formatPercent(intl, sector.occupancyPct),
      }))

  const sectorsShown = data ? sectorRows(data.occupationBySector) : []

  return (
    <Sidebar
      visible={day !== null}
      position="right"
      onHide={onHide}
      showCloseIcon={false}
      blockScroll
      className="nv-scope nv-drawer nv-hosp-detail"
      aria-labelledby="nv-hosp-detail-title"
    >
      <div className="nv-drawer-layout">
        <header className="nv-drawer-head">
          <h2 className="nv-modal-title nv-hosp-detail__title" id="nv-hosp-detail-title">{title}</h2>
          <Button
            type="button"
            text
            className="nv-icon nv-tip"
            icon="pi pi-times"
            aria-label={intl.formatMessage({ id: 'HospitalizationSummaryDetailClose' })}
            data-pr-tooltip={intl.formatMessage({ id: 'HospitalizationSummaryDetailClose' })}
            onClick={onHide}
          />
        </header>

        <div className="nv-drawer-body">
          {/* Los números del día vienen del gráfico: se ven al instante, sin esperar la consulta del día */}
          <section className="nv-hosp-detail__section" aria-label={intl.formatMessage({ id: 'HospitalizationSummaryDayKpisLabel' })}>
            <dl className="nv-hosp-detail__kpis">
              <Kpi label={intl.formatMessage({ id: 'HospitalizationSummaryBedsAvailable' })} value={formatCount(intl, point.bedsAvailable)} />
              <Kpi label={intl.formatMessage({ id: 'HospitalizationSummaryBedsOccupied' })} value={formatCount(intl, point.patientDays)} />
              <Kpi label={intl.formatMessage({ id: 'HospitalizationSummaryKpiOccupancy' })} value={formatPercent(intl, point.occupancyPct)} />
              <Kpi label={intl.formatMessage({ id: 'HospitalizationSummaryKpiAdmissions' })} value={formatCount(intl, point.admissions)} />
              <Kpi label={intl.formatMessage({ id: 'HospitalizationSummaryKpiDischarges' })} value={formatCount(intl, point.discharges)} />
            </dl>
          </section>

          {summary.status === 'loading' && (
            <span className="nv-loading nv-hosp-detail__loading" role="status">
              <ProgressSpinner strokeWidth="4" style={{ width: 16, height: 16 }} />
              <span>{intl.formatMessage({ id: 'HospitalizationSummaryLoading' })}</span>
            </span>
          )}
          {summary.status === 'failed' && (
            <div className="nv-hosp-failed" role="status">
              <p className="nv-note">
                <i className="pi pi-info-circle" aria-hidden="true" />
                {intl.formatMessage({ id: 'HospitalizationSummaryDetailFailed' })}
              </p>
              <Button type="button" link label={intl.formatMessage({ id: 'HospitalizationSummaryRetry' })} onClick={summary.retry} />
            </div>
          )}
          {data && (
            <>
              {breakdown('nv-hosp-detail-admissions', 'HospitalizationSummaryDetailAdmissions', data.admissionsByType, point.admissions)}
              {breakdown('nv-hosp-detail-discharges', 'HospitalizationSummaryDetailDischarges', data.dischargesByReason, point.discharges)}
              {sectorsShown.length > 0 && (
                <Section id="nv-hosp-detail-sectors" title={intl.formatMessage({ id: 'HospitalizationSummaryDetailSectors' })}>
                  <HospitalizationBars rows={sectorsShown} ariaLabelledBy="nv-hosp-detail-sectors" />
                </Section>
              )}
            </>
          )}
        </div>

        <footer className="nv-drawer-foot">
          <Button
            type="button"
            className="buttonMain3"
            label={intl.formatMessage({ id: 'HospitalizationSummaryDetailViewOnly' })}
            onClick={() => onViewDay(point.date)}
          />
        </footer>
      </div>
    </Sidebar>
  )
}
